// Email/password on the Firebase SDK. The hosted build has to include a web
// config. There is no API password session to fall back to.

import { config } from "../config";
import { classifySignupError, SignupError } from "./signupErrors";

export interface FirebaseWebConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  appId: string;
}

type FirebaseAuth = import("firebase/auth").Auth;
type FirebaseUser = import("firebase/auth").User;

export function firebaseWebConfig(
  source: {
    apiKey?: string;
    authDomain?: string;
    projectId?: string;
    appId?: string;
  } = config.firebase,
): FirebaseWebConfig | null {
  const fields = [
    source.apiKey,
    source.authDomain,
    source.projectId,
    source.appId,
  ].map((value) => value?.trim() ?? "");
  if (fields.some((value) => value === "")) return null;
  const [apiKey, authDomain, projectId, appId] = fields as [
    string,
    string,
    string,
    string,
  ];
  return { apiKey, authDomain, projectId, appId };
}

let authPromise: Promise<FirebaseAuth> | null = null;

async function authInstance(): Promise<FirebaseAuth> {
  const web = firebaseWebConfig();
  if (!web) throw new Error("Firebase web config is absent");
  if (!authPromise) {
    authPromise = (async () => {
      const { getApps, initializeApp } = await import("firebase/app");
      const { getAuth } = await import("firebase/auth");
      const existing = getApps()[0];
      const app = existing ?? initializeApp(web);
      return getAuth(app);
    })();
  }
  return authPromise;
}

export async function createAccountForIdToken(
  email: string,
  password: string,
): Promise<string> {
  if (!firebaseWebConfig()) throw new Error("Sign-in is not configured");
  const { createUserWithEmailAndPassword } = await import("firebase/auth");
  const auth = await authInstance();
  try {
    const cred = await createUserWithEmailAndPassword(
      auth,
      email.trim(),
      password,
    );
    return await cred.user.getIdToken();
  } catch (err) {
    throw accountError(err);
  }
}

function accountError(err: unknown): SignupError {
  if (err instanceof SignupError) return err;
  return new SignupError(classifySignupError(err), { cause: err });
}

export async function signInForIdToken(
  email: string,
  password: string,
): Promise<string | null> {
  if (!firebaseWebConfig()) return null;
  try {
    const { signInWithEmailAndPassword } = await import("firebase/auth");
    const auth = await authInstance();
    const cred = await signInWithEmailAndPassword(auth, email.trim(), password);
    return await cred.user.getIdToken();
  } catch {
    return null;
  }
}

export async function readyFirebaseUser(): Promise<FirebaseUser | null> {
  if (!firebaseWebConfig()) return null;
  const auth = await authInstance();
  await auth.authStateReady();
  return auth.currentUser;
}

export async function refreshFirebaseIdToken(): Promise<string | null> {
  const user = await readyFirebaseUser();
  if (!user) return null;
  return user.getIdToken(true);
}

export async function sendPasswordReset(email: string): Promise<void> {
  if (!firebaseWebConfig()) throw new Error("Sign-in is not configured");
  const { sendPasswordResetEmail } = await import("firebase/auth");
  const auth = await authInstance();
  await sendPasswordResetEmail(auth, email.trim());
}

// The handler page calls these with the code it already took off the URL.
// Nothing here builds a link or writes the code anywhere but the SDK call.
export async function verifyPasswordResetCode(code: string): Promise<string> {
  if (!firebaseWebConfig()) throw new Error("Sign-in is not configured");
  const { verifyPasswordResetCode: verify } = await import("firebase/auth");
  const auth = await authInstance();
  return verify(auth, code);
}

export async function confirmPasswordReset(
  code: string,
  password: string,
): Promise<void> {
  if (!firebaseWebConfig()) throw new Error("Sign-in is not configured");
  const { confirmPasswordReset: confirm } = await import("firebase/auth");
  const auth = await authInstance();
  await confirm(auth, code, password);
}

export async function deleteCurrentFirebaseUser(): Promise<void> {
  const { deleteUser } = await import("firebase/auth");
  const auth = await authInstance();
  if (!auth.currentUser) return;
  await deleteUser(auth.currentUser);
}

export async function signOutFirebase(): Promise<void> {
  if (!firebaseWebConfig()) return;
  const { signOut } = await import("firebase/auth");
  const auth = await authInstance();
  await signOut(auth);
}
